import { z } from 'zod';
import { foodSourceSchema, nutrientsSchema } from '../nutrients.js';
import { booleanParam, nutritionPerSchema } from '../units.js';
export const foodSchema = z.object({
    id: z.string().uuid(),
    tenantId: z.string().uuid().nullable(),
    source: foodSourceSchema,
    sourceRef: z.string().nullable(),
    name: z.string(),
    brand: z.string().nullable(),
    category: z.string().nullable(),
    isLiquid: z.boolean(),
    densityGPerMl: z.number().nullable(),
    typicalUnitWeightG: z.number().nullable(),
    /** What one of them is called, singular: "crumpet". Null when it has no name. */
    unitName: z.string().nullable(),
    servingSizeG: z.number().nullable(),
    quality: z.enum(['ok', 'suspect']),
    /** Null until a person has read the packet and agreed (ADR-018). */
    checkedAt: z.string().datetime().nullable().default(null),
    /** As printed. Allergens are deliberately not derived from it. */
    ingredientsText: z.string().nullable().default(null),
    /** What the whole packet weighs. */
    packSizeG: z.number().nullable().default(null),
    /** How many units are in the pack; 8 for an eight-pack of cans. */
    packUnits: z.number().int().positive().nullable().default(null),
    attribution: z.string().nullable(),
    /**
     * Whether the per-100g figures describe this food as sold or as eaten.
     *
     * A label may give either. 257 kcal per 100 g of cooked chips is not
     * 257 kcal per 100 g of the frozen ones in the bag, so wherever a figure
     * is shown, which one it is has to travel with it.
     */
    nutritionBasis: z.enum(['as_sold', 'prepared']).default('as_sold'),
    /**
     * What the figures are per: 100 g (or 100 ml), or one item named by
     * `unitName` when nobody knows what one weighs (ADR-028). A per-item food
     * is counted, never weighed.
     */
    nutritionPer: nutritionPerSchema.default('weight'),
    nutrients: nutrientsSchema,
    /**
     * True when the household has some of this in a fridge, freezer or
     * cupboard right now (M-31). Search puts these first, and the sheet says
     * so, because the food you already own is usually the one you are about
     * to eat.
     */
    inKitchen: z.boolean().default(false),
    measures: z.array(z.object({ label: z.string(), grams: z.number() })).default([]),
    barcodes: z.array(z.string()).default([]),
});
export const foodSearchQuerySchema = z.object({
    q: z.string().min(1).max(120),
    limit: z.coerce.number().int().min(1).max(50).default(20),
    includeBranded: booleanParam(true),
});
/** "Your usual": the things one person logs most (M-14). */
export const frequentQuerySchema = z.object({
    memberId: z.string().uuid(),
    limit: z.coerce.number().int().min(1).max(25).default(10),
});
export const createFoodRequestSchema = z.object({
    name: z.string().min(1).max(200),
    brand: z.string().max(120).nullish(),
    category: z.string().max(120).nullish(),
    isLiquid: z.boolean().default(false),
    densityGPerMl: z.number().positive().nullish(),
    typicalUnitWeightG: z.number().positive().nullish(),
    /** What one of them is called, singular: "crumpet". Only useful with a weight. */
    unitName: z.string().min(1).max(30).nullish(),
    servingSizeG: z.number().positive().nullish(),
    barcode: z.string().min(6).max(20).nullish(),
    /**
     * Whether the figures being given describe the food as sold or as eaten.
     *
     * Somebody copying a label that states its numbers for the cooked product
     * needs to be able to say so, or the figure passes for a raw one.
     */
    nutritionBasis: z.enum(['as_sold', 'prepared']).default('as_sold'),
    /** Figures per 100 g/ml, or per one item when no weight is known (ADR-028). */
    nutritionPer: nutritionPerSchema.default('weight'),
    nutrients: nutrientsSchema,
    measures: z.array(z.object({ label: z.string().min(1), grams: z.number().positive() })).default([]),
}).refine((v) => v.nutritionPer === 'weight' || v.typicalUnitWeightG == null, {
    message: 'A food counted per item has no weight; give its figures per 100 g instead',
});
export const foodOverrideRequestSchema = z.object({
    nutrients: nutrientsSchema,
    note: z.string().max(500).nullish(),
});
//# sourceMappingURL=food.js.map